<template>
  <van-popup
    :value="visible"
    position="right"
    :get-container="getPopupContainer"
    :overlay="true"
    :lock-scroll="true"
    overlay-class="eni-wallet-overlay"
    class="eni-wallet-popup"
    @input="updateVisible"
  >
    <div class="eni-wallet-panel">
      <div class="eni-wallet-header">
        <div>
          <div class="eni-wallet-title">{{ $t("walletDrawer.title") }}</div>
          <div class="eni-wallet-subtitle">
            {{ $t("walletDrawer.subtitle") }}
          </div>
        </div>

        <button
          class="eni-wallet-close"
          @click="closeDrawer"
          type="button"
          aria-label="close"
          :disabled="wallet.state.connecting"
        >
          ✕
        </button>
      </div>

      <!-- 连接状态 -->
      <div v-if="statusText" class="eni-wallet-status">
        <span v-if="wallet.state.connecting" class="eni-wallet-status-spin"></span>
        <span class="eni-wallet-status-text">{{ statusText }}</span>
      </div>

      <div class="eni-wallet-hero">
        <div class="eni-wallet-hero-badge">{{ $t("walletDrawer.heroBadge") }}</div>
        <div class="eni-wallet-hero-title">{{ $t("walletDrawer.heroTitle") }}</div>
        <div class="eni-wallet-hero-desc">
          {{ $t("walletDrawer.heroDesc") }}
        </div>
      </div>

      <div class="eni-wallet-section-title">{{ $t("walletDrawer.recommended") }}</div>

      <div
        class="eni-wallet-featured"
        :class="{ disabled: wallet.state.connecting }"
        @click="chooseWallet(featuredWallet)"
      >
        <div class="eni-wallet-featured-left">
          <div class="eni-wallet-featured-icon-wrap">
            <img
              class="eni-wallet-featured-icon"
              :src="featuredWallet.icon"
              :alt="featuredWallet.name"
            />
          </div>

          <div class="eni-wallet-featured-meta">
            <div class="eni-wallet-featured-name">{{ featuredWallet.name }}</div>
            <div class="eni-wallet-featured-text">{{ featuredWallet.desc }}</div>
          </div>
        </div>

        <div class="eni-wallet-featured-action">
          {{ wallet.state.connecting ? $t("walletDrawer.connecting") : $t("walletDrawer.connect") }}
        </div>
      </div>

      <div class="eni-wallet-section-title eni-wallet-section-title-list">
        {{ $t("walletDrawer.popularWallets") }}
      </div>

      <div class="eni-wallet-list">
        <div
          v-for="item in walletOptions"
          :key="item.key"
          class="eni-wallet-item"
          :class="{ disabled: wallet.state.connecting }"
          @click="chooseWallet(item)"
        >
          <div class="eni-wallet-item-left">
            <div class="eni-wallet-item-icon-wrap">
              <img class="eni-wallet-item-icon" :src="item.icon" :alt="item.name" />
            </div>

            <div class="eni-wallet-item-meta">
              <div class="eni-wallet-item-name">{{ item.name }}</div>
              <div class="eni-wallet-item-desc">{{ item.desc }}</div>
            </div>
          </div>

          <div class="eni-wallet-item-arrow">›</div>
        </div>
      </div>

      <div class="eni-wallet-footer-tip">
        {{ footerTip }}
      </div>
    </div>
  </van-popup>
</template>

<script>
import wallet from "@/store/wallet";

import tokenpocketIcon from "@/assets/icons/tokenpocket.png";
import okxIcon from "@/assets/icons/okx.png";
import safepalIcon from "@/assets/icons/safepal.png";
import trustIcon from "@/assets/icons/trust.png";
import metamaskIcon from "@/assets/icons/metamask.png";
import binanceIcon from "@/assets/icons/binance.png";

export default {
  name: "HeaderWalletDrawer",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const walletOptions = [
      {
        key: "tokenpocket",
        name: "TokenPocket",
        desc: this.$t("walletDrawer.wallets.tokenpocket"),
        icon: tokenpocketIcon
      },
      {
        key: "okx",
        name: "OKX Wallet",
        desc: this.$t("walletDrawer.wallets.okx"),
        icon: okxIcon
      },
      {
        key: "safepal",
        name: "SafePal",
        desc: this.$t("walletDrawer.wallets.safepal"),
        icon: safepalIcon
      },
      {
        key: "trust",
        name: "Trust Wallet",
        desc: this.$t("walletDrawer.wallets.trust"),
        icon: trustIcon
      },
      {
        key: "metamask",
        name: "MetaMask",
        desc: this.$t("walletDrawer.wallets.metamask"),
        icon: metamaskIcon
      },
      {
        key: "binance",
        name: "Binance Wallet",
        desc: this.$t("walletDrawer.wallets.binance"),
        icon: binanceIcon
      }
    ];

    return {
      wallet,
      walletOptions,
      featuredWallet: walletOptions[0]
    };
  },
  computed: {
    statusText() {
      if (!this.wallet?.state?.connecting) return "";

      const step = this.wallet.state.connectStep;
      if (step === "detect") return this.$t("walletDrawer.status.detect");
      if (step === "accounts") return this.$t("walletDrawer.status.accounts");
      if (step === "switch") return this.$t("walletDrawer.status.switch");
      if (step === "sign") return this.$t("walletDrawer.status.sign");
      if (step === "login") return this.$t("walletDrawer.status.login");
      return this.$t("walletDrawer.status.default");
    },

    footerTip() {
      if (this.wallet?.state?.connecting) {
        return this.statusText || this.$t("walletDrawer.status.default");
      }
      return this.$t("walletDrawer.footer");
    }
  },
  watch: {
    "$i18n.locale"() {
      this.walletOptions = [
        {
          key: "tokenpocket",
          name: "TokenPocket",
          desc: this.$t("walletDrawer.wallets.tokenpocket"),
          icon: tokenpocketIcon
        },
        {
          key: "okx",
          name: "OKX Wallet",
          desc: this.$t("walletDrawer.wallets.okx"),
          icon: okxIcon
        },
        {
          key: "safepal",
          name: "SafePal",
          desc: this.$t("walletDrawer.wallets.safepal"),
          icon: safepalIcon
        },
        {
          key: "trust",
          name: "Trust Wallet",
          desc: this.$t("walletDrawer.wallets.trust"),
          icon: trustIcon
        },
        {
          key: "metamask",
          name: "MetaMask",
          desc: this.$t("walletDrawer.wallets.metamask"),
          icon: metamaskIcon
        },
        {
          key: "binance",
          name: "Binance Wallet",
          desc: this.$t("walletDrawer.wallets.binance"),
          icon: binanceIcon
        }
      ];
      this.featuredWallet = this.walletOptions[0];
    }
  },
  methods: {
    getPopupContainer() {
      return document.body;
    },

    updateVisible(val) {
      this.$emit("update:visible", val);
    },

    closeDrawer() {
      if (this.wallet?.state?.connecting) return;
      this.$emit("update:visible", false);
    },

    async chooseWallet(item) {
      if (!item || this.wallet?.state?.connecting) return;

      const choiceMap = {
        tokenpocket: "TokenPocket",
        metamask: "Metamask",
        okx: "OKX",
        binance: "Binance",
        trust: "Trust",
        safepal: "SafePal"
      };

      const choice = choiceMap[item.key] || "Auto";

      try {
        const ret = await this.wallet.connectAndLogin(choice);

        this.$emit("select", item);
        this.$emit("success", {
          wallet: item,
          auth: ret || {}
        });

        this.$emit("update:visible", false);

        window.dispatchEvent(
          new CustomEvent("auth:login", {
            detail: ret || {}
          })
        );

        this.toast(
          {
            title: this.$t("walletDrawer.toast.successTitle"),
            desc: this.$t("walletDrawer.toast.successDesc", { name: item.name }),
            duration: 2200
          },
          "success"
        );
      } catch (e) {
        this.toast(
          {
            title: this.$t("walletDrawer.toast.failTitle"),
            desc: e?.message || this.$t("walletDrawer.toast.failDesc"),
            duration: 3200
          },
          "error"
        );
      }
    },

    toast(msg, type = "info") {
      if (this.$toast) {
        if (typeof msg === "string") {
          if (typeof this.$toast[type] === "function") return this.$toast[type](msg);
          if (typeof this.$toast.show === "function") return this.$toast.show(msg);
        } else {
          if (typeof this.$toast.show === "function") return this.$toast.show(msg);
        }
      }

      const fallback = typeof msg === "string" ? msg : (msg?.desc || msg?.title || this.$t("walletDrawer.toast.notice"));
      alert(fallback);
    }
  }
};
</script>

<style lang="less">
.eni-wallet-overlay {
  z-index: 6099 !important;
  background: rgba(15, 23, 42, 0.46) !important;
}

.eni-wallet-popup {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 88vw !important;
  max-width: 420px !important;
  height: 100vh !important;
  background: #ffffff !important;
  z-index: 6100 !important;
  overflow: hidden !important;
  transform: translate3d(0, 0, 0) !important;
}

.van-popup--right {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  height: 100vh !important;
  z-index: 6100 !important;
}

.eni-wallet-panel {
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  background:
    radial-gradient(circle at top right, rgba(94, 151, 255, 0.14), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  padding: 22px 18px 28px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.eni-wallet-panel,
.eni-wallet-panel * {
  box-sizing: border-box;
  color: #17253a;
}

.eni-wallet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.eni-wallet-title {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
  color: #132238;
}

.eni-wallet-subtitle {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #7f90aa;
  max-width: 260px;
}

.eni-wallet-close {
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 24px;
  line-height: 1;
  color: #667897;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.eni-wallet-close:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.eni-wallet-status {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f5f9ff 0%, #eef5ff 100%);
  border: 1px solid #d9e8ff;
  display: flex;
  align-items: center;
  gap: 10px;
}

.eni-wallet-status-text {
  font-size: 12px;
  line-height: 1.5;
  color: #5c6f8d !important;
}

.eni-wallet-status-spin {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(80, 125, 220, 0.18);
  border-top-color: #3f86ff;
  animation: eni-wallet-spin .7s linear infinite;
  flex-shrink: 0;
}

@keyframes eni-wallet-spin {
  to {
    transform: rotate(360deg);
  }
}

.eni-wallet-hero {
  margin-top: 22px;
  padding: 18px 16px;
  border-radius: 22px;
  background:
    radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 30%),
    linear-gradient(135deg, #3d83ff 0%, #67a8ff 100%);
  box-shadow: 0 16px 34px rgba(62, 117, 220, 0.22);
}

.eni-wallet-hero-badge {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  color: #ffffff !important;
  font-size: 12px;
  font-weight: 700;
}

.eni-wallet-hero-title {
  margin-top: 14px;
  color: #ffffff !important;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
}

.eni-wallet-hero-desc {
  margin-top: 10px;
  color: rgba(255,255,255,0.92) !important;
  font-size: 13px;
  line-height: 1.65;
}

.eni-wallet-section-title {
  margin-top: 24px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #8b9ab2 !important;
}

.eni-wallet-section-title-list {
  margin-top: 22px;
}

.eni-wallet-featured {
  min-height: 82px;
  border-radius: 20px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #deebff;
  background: linear-gradient(180deg, #ffffff 0%, #f6faff 100%);
  box-shadow: 0 10px 26px rgba(76, 125, 214, 0.08);
  cursor: pointer;
  transition: all .18s ease;
}

.eni-wallet-featured:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(76, 125, 214, 0.12);
}

.eni-wallet-featured-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.eni-wallet-featured-icon-wrap,
.eni-wallet-item-icon-wrap {
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e8f0ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(77, 123, 210, 0.08);
}

.eni-wallet-featured-icon,
.eni-wallet-item-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
  display: block;
}

.eni-wallet-featured-meta,
.eni-wallet-item-meta {
  min-width: 0;
}

.eni-wallet-featured-name,
.eni-wallet-item-name {
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  color: #17253a;
}

.eni-wallet-featured-text,
.eni-wallet-item-desc {
  margin-top: 5px;
  font-size: 12px;
  line-height: 1.45;
  color: #8393aa;
}

.eni-wallet-featured-action {
  height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #3f86ff 0%, #67a8ff 100%);
  color: #ffffff !important;
  font-size: 13px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.eni-wallet-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.eni-wallet-item {
  min-height: 76px;
  border-radius: 20px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #e4edff;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  cursor: pointer;
  transition: all .18s ease;
}

.eni-wallet-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(76, 125, 214, 0.08);
}

.eni-wallet-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.eni-wallet-item-arrow {
  font-size: 22px;
  color: #8a98af;
  flex-shrink: 0;
}

.eni-wallet-footer-tip {
  margin-top: 18px;
  padding-bottom: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #8a98af;
}

.disabled {
  opacity: .68;
  cursor: not-allowed !important;
  pointer-events: none;
}

@media (max-width: 375px) {
  .eni-wallet-popup {
    width: 90vw !important;
  }

  .eni-wallet-panel {
    padding: 20px 16px 24px;
  }

  .eni-wallet-title {
    font-size: 22px;
  }

  .eni-wallet-hero-title {
    font-size: 22px;
  }

  .eni-wallet-featured-name,
  .eni-wallet-item-name {
    font-size: 15px;
  }
}
</style>